/**
 * API connections — every saved API a "Call API" step can use. Creating and editing
 * happen on the dedicated editor page (ApiConnectionEditor), not in a popup.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Link,
  alpha,
} from '@mui/material';
import { Add, DeleteOutline, ChevronRight, Lock, Search, Webhook, VpnKey, DataObject, Bolt, MenuBook } from '@mui/icons-material';
import { usePlanFeature } from '@/hooks/usePlanFeature';
import apiConnectionApi, { ApiConnection } from '@/services/apiConnection.api';
import { joinEndpoint } from './apiConnectionUtils';
import ApiConnectionGuide from './ApiConnectionGuide';

const AUTH_LABELS: Record<ApiConnection['auth']['type'], string> = {
  none: 'No authentication',
  bearer: 'Bearer token',
  basic: 'Basic auth',
  api_key_header: 'API key',
  hmac: 'HMAC signed',
  oauth2_client_credentials: 'OAuth2',
  external_client: 'Signed by external client',
};

const COLS = 'minmax(220px,1.6fr) minmax(260px,2.4fr) 110px 100px 84px';
const MONO = { fontFamily: 'monospace', fontSize: 12.5 } as const;

const ApiConnections: React.FC = () => {
  const navigate = useNavigate();
  const prefix = useLocation().pathname.startsWith('/admin') ? '/admin' : '';
  const base = `${prefix}/automation/api-connections`;
  const { hasFeature } = usePlanFeature();
  const enabled = hasFeature('httpAutomationEnabled');

  const [items, setItems] = useState<ApiConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ApiConnection | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'disabled'>('all');
  const [guideOpen, setGuideOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await apiConnectionApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load APIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
    else setLoading(false);
  }, [enabled, load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      if (status !== 'all' && c.status !== status) return false;
      if (!q) return true;
      return [c.name, c.description ?? '', c.baseUrl].join(' ').toLowerCase().includes(q);
    });
  }, [items, query, status]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiConnectionApi.remove(confirmDelete.id);
      setNotice({ severity: 'success', message: 'API deleted.' });
      await load();
    } catch (e: any) {
      setNotice({ severity: 'error', message: e?.response?.data?.message ?? 'Delete failed' });
    } finally {
      setConfirmDelete(null);
    }
  };

  if (!enabled) {
    return (
      <Box>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          API connections
        </Typography>
        <Alert severity="info" icon={<Lock />} sx={{ mt: 2 }}>
          HTTP API Automation isn’t included in your current plan. Upgrade to connect your flows to external APIs.
        </Alert>
      </Box>
    );
  }

  const activeCount = items.filter((c) => c.status === 'active').length;
  const tile = (icon: React.ReactNode) => (
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'text.secondary', flexShrink: 0 }}>{icon}</Box>
  );

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', display: 'flex', flexDirection: 'column', minHeight: { xs: 'calc(100vh - 88px)', sm: 'calc(100vh - 104px)', md: 'calc(100vh - 48px)', lg: 'calc(100vh - 64px)' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Integrations
          </Typography>
          <Typography fontSize={32} fontWeight={700} lineHeight={1.15}>
            API connections
          </Typography>
          <Typography color="text.secondary" mt={0.5}>
            Reusable requests and response variables for your automations.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button variant="outlined" startIcon={<MenuBook />} onClick={() => setGuideOpen(true)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}>
            Guide
          </Button>
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => navigate(`${base}/new`)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}>
            New connection
          </Button>
        </Stack>
      </Stack>

      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice.message}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2.5, borderTop: 1, borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        {tile(<Webhook fontSize="small" />)}
        <Box>
          <Typography fontWeight={700}>
            {items.length} {items.length === 1 ? 'connection' : 'connections'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {activeCount} active · Ready for your workflows
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search connections…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
        />
        <TextField select size="small" value={status} onChange={(e) => setStatus(e.target.value as typeof status)} sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
        </TextField>
      </Stack>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper', overflow: 'auto' }}>
        <Box sx={{ minWidth: 820 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: COLS, columnGap: 2, px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
            {['CONNECTION', 'REQUEST', 'VARIABLES', 'STATUS', ''].map((h) => (
              <Typography key={h || 'actions'} sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: 'text.secondary' }}>
                {h}
              </Typography>
            ))}
          </Box>

          {loading ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : rows.length === 0 ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 7, textAlign: 'center', color: 'text.secondary' }}>
              <Webhook sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography>{items.length === 0 ? 'No connections yet.' : 'No connections match these filters.'}</Typography>
              {items.length === 0 && (
                <>
                  <Typography variant="body2">Create one, test it against the real endpoint, and map what it returns.</Typography>
                  <Button variant="outlined" startIcon={<Add />} onClick={() => navigate(`${base}/new`)} sx={{ textTransform: 'none' }}>
                    Create your first connection
                  </Button>
                </>
              )}
            </Stack>
          ) : (
            rows.map((c, idx) => {
              const mapped = c.request?.responseMappings?.length ?? 0;
              const endpoint = c.baseUrl ? joinEndpoint(c.baseUrl, c.request?.path, c.request?.queryParams).replace(/^https?:\/\//, '') : '';
              return (
                <Box
                  key={c.id}
                  onClick={() => navigate(`${base}/${c.id}`)}
                  sx={{ display: 'grid', gridTemplateColumns: COLS, columnGap: 2, alignItems: 'center', px: 3, py: 2, cursor: 'pointer', borderTop: idx === 0 ? 0 : 1, borderColor: 'divider', opacity: c.status === 'active' ? 1 : 0.7, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    {tile(<Webhook fontSize="small" />)}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>
                        {c.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {c.description || 'No description yet'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box sx={{ ...MONO, fontSize: 11, fontWeight: 700, px: 0.9, py: 0.25, borderRadius: '6px', bgcolor: 'action.selected', flexShrink: 0 }}>{c.request?.method ?? 'GET'}</Box>
                      <Typography noWrap sx={MONO}>
                        {endpoint || 'No endpoint yet'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      <VpnKey sx={{ fontSize: 13 }} />
                      <Typography variant="caption">{AUTH_LABELS[c.auth.type]}</Typography>
                    </Stack>
                  </Box>

                  <Typography variant="body2" color={mapped ? 'text.primary' : 'text.secondary'}>
                    {mapped} mapped
                  </Typography>

                  <Box>
                    <Chip
                      size="small"
                      label={c.status === 'active' ? 'Active' : 'Disabled'}
                      sx={{
                        fontWeight: 600,
                        bgcolor: (t) => (c.status === 'active' ? alpha(t.palette.success.main, 0.12) : t.palette.action.selected),
                        color: (t) => (c.status === 'active' ? t.palette.success.dark : t.palette.text.secondary),
                      }}
                    />
                  </Box>

                  <Stack direction="row" justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setConfirmDelete(c)} aria-label="Delete connection">
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => navigate(`${base}/${c.id}`)} aria-label="Open connection">
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* Footer — always at the bottom of the page, however few connections there are. */}
      <Box sx={{ mt: 'auto', pt: 5 }}>
      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap sx={{ color: 'text.secondary' }}>
        {[
          { icon: <Webhook fontSize="small" />, label: 'Connect a service' },
          { icon: <DataObject fontSize="small" />, label: 'Map its response' },
          { icon: <Bolt fontSize="small" />, label: 'Use it in a workflow' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.label}>
            <Stack direction="row" spacing={1} alignItems="center">
              {tile(s.icon)}
              <Typography variant="body2">{s.label}</Typography>
            </Stack>
            {i < arr.length - 1 && <ChevronRight fontSize="small" />}
          </React.Fragment>
        ))}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Link component="button" type="button" underline="hover" onClick={() => navigate(`${prefix}/automation/workflows`)} sx={{ fontSize: 14 }}>
          View workflows →
        </Link>
      </Box>
      </Box>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete connection?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{confirmDelete?.name}</strong>? Workflow steps using it will be skipped until you pick another connection.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ApiConnectionGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Box>
  );
};

export default ApiConnections;
